import * as React from 'react';
import { isTypingTarget } from '@/components/ui/workspace';
import { cn } from '@/lib/utils/cn';
import { ReportBlock, type BlockMode } from './report-block';
import type { TokenValues } from './tokens';
import type { EditorBlock, EditorCitation, EditorSection, ReportEditorCalls } from './types';

/**
 * The shared report block editor (E14-design §5; ADR-025 decision 7;
 * T-0271). A report is sections of blocks; this edits one block at a time,
 * by hand or by asking the AI, and knows nothing of the report behind it.
 * Its props are the sections with their blocks, the token values, and the
 * three calls: rewrite (returns a proposal, saves nothing), save and
 * revert. Tables and figures the report's code composes ride along as each
 * section's `before` and `after`.
 *
 * Keyboard, as the requirements grid's: `j` / `k` move between blocks, `e`
 * edits the focused block, `r` rewrites it, `Esc` closes. Keys work while
 * focus is inside the editor, so a screen with other keyboard parts keeps
 * them. A read-only editor (a published report) only moves.
 */
export interface ReportEditorProps extends ReportEditorCalls {
  sections: EditorSection[];
  tokens: TokenValues;
  readOnly?: boolean;
  /** The figure chips' hover text: where the numbers come from. */
  tokenSource?: string;
  onOpenCitation?: (citation: EditorCitation, block: EditorBlock) => void;
  className?: string;
  'aria-label'?: string;
}

export function ReportEditor({
  sections,
  tokens,
  readOnly = false,
  tokenSource,
  onRewrite,
  onSave,
  onRevert,
  onOpenCitation,
  className,
  'aria-label': ariaLabel = 'Report',
}: ReportEditorProps) {
  const [focusedId, setFocusedId] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState<{ id: string; mode: BlockMode } | null>(null);
  const refs = React.useRef(new Map<string, HTMLDivElement>());
  const order = React.useMemo(() => sections.flatMap((s) => s.blocks.map((b) => b.id)), [sections]);
  const calls = React.useMemo(
    () => ({ onRewrite, onSave, onRevert }),
    [onRewrite, onSave, onRevert],
  );

  // A block that left the report (a redraft) takes its focus and its open mode with it.
  const focused = focusedId !== null && order.includes(focusedId) ? focusedId : null;
  const openNow = open && order.includes(open.id) && !readOnly ? open : null;

  const focusBlock = (id: string) => {
    setFocusedId(id);
    const el = refs.current.get(id);
    el?.focus();
    el?.scrollIntoView?.({ block: 'nearest' });
  };

  const setMode = (id: string, mode: BlockMode) => {
    if (mode === 'read') {
      setOpen(null);
      // Back on the block, so j / k carry on from it.
      requestAnimationFrame(() => refs.current.get(id)?.focus());
    } else {
      setFocusedId(id);
      setOpen({ id, mode });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    if (isTypingTarget(e.target)) return;
    if (e.key === 'Escape') {
      if (openNow) {
        e.preventDefault();
        setMode(openNow.id, 'read');
      }
      return;
    }
    if (order.length === 0) return;
    if (e.key === 'j' || e.key === 'k') {
      e.preventDefault();
      const at = focused === null ? -1 : order.indexOf(focused);
      const next =
        at < 0
          ? e.key === 'j'
            ? 0
            : order.length - 1
          : Math.min(Math.max(at + (e.key === 'j' ? 1 : -1), 0), order.length - 1);
      focusBlock(order[next]!);
      return;
    }
    if (readOnly || focused === null) return;
    if (e.key === 'e' || e.key === 'r') {
      e.preventDefault();
      setMode(focused, e.key === 'e' ? 'edit' : 'rewrite');
    }
  };

  const tabbableId = focused ?? order[0] ?? null;

  return (
    <div
      role="region"
      aria-label={ariaLabel}
      className={cn('flex flex-col gap-6', className)}
      onKeyDown={onKeyDown}
    >
      {sections.map((section) => (
        <section
          key={section.key}
          {...(section.titleHidden
            ? { 'aria-label': section.title }
            : { 'aria-labelledby': `report-section-${section.key}` })}
          className={cn('flex flex-col gap-2', section.className)}
        >
          {section.titleHidden ? null : (
            <header className="flex flex-col gap-0.5">
              <h2
                id={`report-section-${section.key}`}
                className="m-0 font-serif text-h2 font-semibold text-ink-900"
              >
                {section.title}
              </h2>
              {section.description ? (
                <p className="m-0 text-meta text-ink-500">{section.description}</p>
              ) : null}
            </header>
          )}
          {section.before}
          {section.blocks.map((block, i) => {
            const group = block.group ?? null;
            const opensGroup = group !== null && section.blocks[i - 1]?.group?.id !== group.id;
            return (
              <React.Fragment key={block.id}>
                {opensGroup ? (
                  <div className="mt-2 flex flex-col gap-0.5 px-3">
                    <h3 className="m-0 text-h3 font-semibold text-ink-900">{group.title}</h3>
                    {group.note ? <p className="m-0 text-meta text-ink-500">{group.note}</p> : null}
                  </div>
                ) : null}
                <ReportBlock
                  ref={(el) => {
                    if (el) refs.current.set(block.id, el);
                    else refs.current.delete(block.id);
                  }}
                  block={block}
                  tokens={tokens}
                  tokenSource={tokenSource}
                  readOnly={readOnly}
                  focused={focused === block.id}
                  tabbable={tabbableId === block.id}
                  mode={openNow?.id === block.id ? openNow.mode : 'read'}
                  calls={calls}
                  onMode={(mode) => setMode(block.id, mode)}
                  onFocus={() => setFocusedId(block.id)}
                  onOpenCitation={onOpenCitation ? (c) => onOpenCitation(c, block) : undefined}
                />
              </React.Fragment>
            );
          })}
          {section.after}
        </section>
      ))}
    </div>
  );
}
